import { useCallback, useMemo, useRef, useState } from 'react';

import { hasSelectionInUrl, smartBack } from '~helpers';
import { useProductCategoryResolver } from '~hooks';
import { ProductDetails } from '~organisms';
import { ErrorPage } from '~templates';

/**
 * ProductDetailsWidget — React component rendered inside the product-details-widget custom element.
 *
 * Requires a `data-product-category-shortname` attribute on the host element.
 * If absent or if the API returns an error, the widget renders an error page.
 *
 * Drawer animation is handled by PurchaseDrawer (deferred open).
 * The widget only manages a single `isPurchaseDrawerOpen` boolean + `flowKey` for reset.
 */
export const ProductDetailsWidget = () => {
  const initialShortname = useMemo(() => {
    const el = document.querySelector<HTMLElement>('product-details-widget');
    return el?.dataset.productCategoryShortname ?? '';
  }, []);

  // Resolve the product category — API errors propagate to ErrorBoundary → ErrorPage
  const { productCategory, products, loading } = useProductCategoryResolver({
    productCategoryShortname: initialShortname || undefined,
    propagateErrors: true,
  });

  // --- Purchase drawer state (desktop only) ---
  const [isPurchaseDrawerOpen, setIsPurchaseDrawerOpen] = useState(() => hasSelectionInUrl());
  const flowKeyRef = useRef(0);
  const [flowKey, setFlowKey] = useState(0);

  const handleOpenPurchaseDrawer = useCallback(() => {
    setIsPurchaseDrawerOpen(true);
  }, []);

  const handleClosePurchaseDrawer = useCallback(() => {
    setIsPurchaseDrawerOpen(false);
  }, []);

  // Reset flow after close animation completes (same pattern as Purchase widget)
  const handleExitTransitionEnd = useCallback(() => {
    flowKeyRef.current += 1;
    setFlowKey(flowKeyRef.current);
  }, []);

  // Missing shortname → error page
  if (!initialShortname) {
    return <ErrorPage />;
  }

  return (
    <ProductDetails
      productCategory={productCategory}
      products={products}
      isLoadingProductCategory={loading}
      initialCategoryShortname={initialShortname}
      onBackButtonClick={smartBack}
      isPurchaseDrawerOpen={isPurchaseDrawerOpen}
      onOpenPurchaseDrawer={handleOpenPurchaseDrawer}
      onClosePurchaseDrawer={handleClosePurchaseDrawer}
      onExitTransitionEnd={handleExitTransitionEnd}
      flowKey={flowKey}
    />
  );
};
